/**
 * Symbol and batch-related types
 */

export interface SessionWindow {
  startMinutes: number;
  endMinutes: number;
}

export interface DaySessions {
  windows: SessionWindow[];
}

export interface SymbolSessions {
  /** Index 0 = Sunday … 6 = Saturday */
  quoteSessions: DaySessions[];
  tradeSessions: DaySessions[];
}

export interface CommissionTier {
  unitType: number;
  unitMode: number;
  unitValue: number;
  minValue: number;
  maxValue: number;
  minUnit: number;
  maxUnit: number;
  currencyUnit: string;
}

export interface SymbolCommission {
  groupPattern: string;
  type: number;
  period: number;
  moneyCurrency: string;
  instantType: number;
  tiers: CommissionTier[];
}

export interface SymbolGroupTemplate {
  groupName: string;
  deviationRate: number;
  roundRate: number;
  orderFlags: number;
  priceTimeout: number;
  requoteTimeout: number;
  requestLots: number;
  minLots: number;
  minVolume: number;
  maxLots: number;
  maxVolume: number;
  lotsStep: number;
  volumeStep: number;
  initialMargin: number;
  maintenanceMargin: number;
  initMarginRate: number[];
  mntnMarginRate: number[];
  marginFlags: number;
  hedgedMargin: number;
  hedgedMarginRate: number;
  swapType: number;
  swapLong: number;
  swapShort: number;
  swapRates: number[];
  tradeMode: number;
  tradeType: number;
  fillPolicy: number;
  expiration: number;
  v3DaysSwap: number;
  sl: number;
  tp: number;
}

export interface Symbol {
  currency: string;
  symbolCurrency: string;
  updateTime: number;
  description: string;
  basis: string;
  refToSite: string;
  custom: number;
  profitCurrency: string;
  marginCurrency: string;
  fillingFlags: number;
  expirationType: number;
  hedgedMargin: number;
  digits: number;
  points: number;
  limitPoints: number;
  precision: number;
  bkgndColor: number;
  depthOfMarket: number;
  contractSize: number;
  tickValue: number;
  bidTickValue: number;
  askTickValue: number;
  tickSize: number;
  spread: number;
  gtcMode: number;
  calcMode: number;
  settlementPrice: number;
  lowerLimit: number;
  upperLimit: number;
  faceValue: number;
  accruedInterest: number;
  firstTradeTime: number;
  lastTradeTime: number;
  id: number;
  isin: string;
  groupName: string;
  deviationRate: number;
  roundRate: number;
  orderFlags: number;
  priceTimeout: number;
  requoteTimeout: number;
  requestLots: number;
  minLots: number;
  minVolume: number;
  maxLots: number;
  maxVolume: number;
  initialMargin: number;
  maintenanceMargin: number;
  initMarginRate: number[];
  mntnMarginRate: number[];
  marginFlags: number;
  collateralLiquidityRate: number;
  hedgedMarginRate: number;
  marginPrecision: number;
  profitCalcFlags: number;
  swapType: number;
  swapLong: number;
  swapShort: number;
  swapRates: number[];
  tradeMode: number;
  tradeType: number;
  v3DaysSwap: number;
  sl: number;
  tp: number;
  lotsStep: number;
  volumeStep: number;
  sessions?: SymbolSessions | null;
  commission?: SymbolCommission | null;
  matchedGroupTemplates?: string[];
}

export enum VybeCalculationMode {
  Forex = 0,
  Futures = 1,
  CFD = 2,
  CFDIndex = 3,
  CFDLeverage = 4,
  CalcMode5 = 5,
  ExchangeStocks = 32,
  ExchangeFutures = 33,
  FORTSFutures = 34,
  ExchangeOption = 35,
  ExchangeMarginOption = 36,
  ExchangeBounds = 37,
  Collateral = 64,
}

export enum VybeGTCMode {
  Cancelled = 0,
  TodayIncludeSLTP = 1,
  TodayExcludeSLTP = 2,
}

export enum VybeSwapType {
  SwapNone = 0,
  InPoints = 1,
  SymInfoS408 = 2,
  MarginCurrency = 3,
  Currency = 4,
  PercCurPrice = 5,
  PercOpenPrice = 6,
  PointClosePrice = 7,
  PointBidPrice = 8,
}

export enum VybeTradeMode {
  Disabled = 0,
  LongOnly = 1,
  ShortOnly = 2,
  CloseOnly = 3,
  FullAccess = 4,
}

export enum VybeExecutionType {
  Request = 0,
  Instant = 1,
  Market = 2,
  Exchange = 3,
}

export enum VybeV3DaysSwap {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

const calculationModeLabels: Record<number, string> = {
  [VybeCalculationMode.Forex]: 'Forex',
  [VybeCalculationMode.Futures]: 'Futures',
  [VybeCalculationMode.CFD]: 'CFD',
  [VybeCalculationMode.CFDIndex]: 'CFD Index',
  [VybeCalculationMode.CFDLeverage]: 'CFD Leverage',
  [VybeCalculationMode.CalcMode5]: 'Calc Mode 5',
  [VybeCalculationMode.ExchangeStocks]: 'Exchange Stocks',
  [VybeCalculationMode.ExchangeFutures]: 'Exchange Futures',
  [VybeCalculationMode.FORTSFutures]: 'FORTS Futures',
  [VybeCalculationMode.ExchangeOption]: 'Exchange Option',
  [VybeCalculationMode.ExchangeMarginOption]: 'Exchange Margin Option',
  [VybeCalculationMode.ExchangeBounds]: 'Exchange Bounds',
  [VybeCalculationMode.Collateral]: 'Collateral',
};

const gtcModeLabels: Record<number, string> = {
  [VybeGTCMode.Cancelled]: 'Cancelled',
  [VybeGTCMode.TodayIncludeSLTP]: 'Today, include SL/TP',
  [VybeGTCMode.TodayExcludeSLTP]: 'Today, exclude SL/TP',
};

const swapTypeLabels: Record<number, string> = {
  [VybeSwapType.SwapNone]: 'None',
  [VybeSwapType.InPoints]: 'In points',
  [VybeSwapType.SymInfoS408]: 'Symbol info s408',
  [VybeSwapType.MarginCurrency]: 'Margin currency',
  [VybeSwapType.Currency]: 'Symbol currency',
  [VybeSwapType.PercCurPrice]: 'Percent of current price',
  [VybeSwapType.PercOpenPrice]: 'Percent of open price',
  [VybeSwapType.PointClosePrice]: 'Points by close price',
  [VybeSwapType.PointBidPrice]: 'Points by bid price',
};

const tradeModeLabels: Record<number, string> = {
  [VybeTradeMode.Disabled]: 'Disabled',
  [VybeTradeMode.LongOnly]: 'Long only',
  [VybeTradeMode.ShortOnly]: 'Short only',
  [VybeTradeMode.CloseOnly]: 'Close only',
  [VybeTradeMode.FullAccess]: 'Full access',
};

const executionTypeLabels: Record<number, string> = {
  [VybeExecutionType.Request]: 'Request',
  [VybeExecutionType.Instant]: 'Instant',
  [VybeExecutionType.Market]: 'Market',
  [VybeExecutionType.Exchange]: 'Exchange',
};

const v3DaysSwapLabels: Record<number, string> = {
  [VybeV3DaysSwap.Sunday]: 'Sunday',
  [VybeV3DaysSwap.Monday]: 'Monday',
  [VybeV3DaysSwap.Tuesday]: 'Tuesday',
  [VybeV3DaysSwap.Wednesday]: 'Wednesday',
  [VybeV3DaysSwap.Thursday]: 'Thursday',
  [VybeV3DaysSwap.Friday]: 'Friday',
  [VybeV3DaysSwap.Saturday]: 'Saturday',
};

const fillingFlagLabels: Array<[number, string]> = [
  [1, 'FOK'],
  [2, 'IOC'],
  [4, 'BOC'],
];

const expirationFlagLabels: Array<[number, string]> = [
  [1, 'GTC'],
  [2, 'Day'],
  [4, 'Specified'],
  [8, 'Specified day'],
];

function formatEnumValue(value: number | undefined, labels: Record<number, string>): string {
  if (value === undefined || !Number.isFinite(value)) return '—';

  return `${value} - ${labels[value] ?? 'Unknown'}`;
}

function formatFlagValue(
  value: number | undefined,
  labels: Array<[number, string]>
): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  if (value === 0) return '0 - None';

  const names = labels
    .filter(([flag]) => (value & flag) === flag)
    .map(([, label]) => label);

  return `${value} - ${names.length > 0 ? names.join(', ') : 'Unknown'}`;
}

export const formatCalculationMode = (value: number | undefined): string =>
  formatEnumValue(value, calculationModeLabels);

export const formatGTCMode = (value: number | undefined): string =>
  formatEnumValue(value, gtcModeLabels);

export const formatSwapType = (value: number | undefined): string =>
  formatEnumValue(value, swapTypeLabels);

export const formatTradeMode = (value: number | undefined): string =>
  formatEnumValue(value, tradeModeLabels);

export const formatExecutionType = (value: number | undefined): string =>
  formatEnumValue(value, executionTypeLabels);

export const formatV3DaysSwap = (value: number | undefined): string =>
  formatEnumValue(value, v3DaysSwapLabels);

export const formatFillingFlags = (value: number | undefined): string =>
  formatFlagValue(value, fillingFlagLabels);

export const formatExpirationFlags = (value: number | undefined): string =>
  formatFlagValue(value, expirationFlagLabels);

export interface SymbolBaseInfo {
  companyName: string;
  currency: string;
  digits: number;
  symbolSet: number[];
  receiveUserMsg: number;
  accountMethod: string;
  marginMode: string;
  symbolsHash: string;
  spreadsHash: string;
}

export interface SymbolsBatch {
  batchNumber: number;
  totalBatches: number;
  totalSymbolsCount: number;
  isFinalBatch: boolean;
  success: boolean;
  errorMessage: string | null;
  symbols: Symbol[];
  groups: SymbolGroupTemplate[];
  baseInfo?: SymbolBaseInfo | null;
}

export interface SymbolsState {
  // High-performance lookups
  byId: Map<number, Symbol>;
  byCurrency: Map<string, Symbol>;
  byGroup: Map<string, Symbol[]>;
  
  // Batch tracking
  loadingStatus: 'idle' | 'loading' | 'complete' | 'error';
  totalExpected: number;
  totalLoaded: number;
  batchesReceived: number;
  totalBatches: number;
  errorMessage: string | null;
  baseInfo: SymbolBaseInfo | null;
  groupTemplates: SymbolGroupTemplate[];
  
  // Sorted arrays for UI (computed once)
  allSymbols: Symbol[];
  groupNames: string[];
}

export interface SymbolsActions {
  processBatch: (batch: SymbolsBatch) => void;
  reset: () => void;
  getSymbolById: (id: number) => Symbol | undefined;
  getSymbolByCurrency: (currency: string) => Symbol | undefined;
  /** Case-insensitive lookup; returns broker-canonical symbol.currency. */
  findSymbolByCurrency: (raw: string) => Symbol | undefined;
  getSymbolsByGroup: (groupName: string) => Symbol[];
  getSymbolsByCurrencies: (currencies: string[]) => Symbol[];
  getSymbolsByQuery: (query: string) => Symbol[];
  getCatalogsByGroup: (path?: string[]) => Array<{
    name: string;
    hasSubgroups: boolean;
    symbolsCount: number;
    path: string[];
  }>;
  getSymbolsByGroupPath: (path: string[]) => Symbol[];
  getGroupTemplates: () => SymbolGroupTemplate[];
  isComplete: () => boolean;
}
